import { KeyLabelPair } from "@/config/types";
import { clsx } from "clsx/lite";
import { useSegmentedControl } from "./segmentedControl/useSegmentedControl";

type SegmentedControlProps<Options extends readonly KeyLabelPair[]> = {
  options: Options;
  value: Options[number]["key"];
  onChange: (value: Options[number]["key"]) => void;
  isDark?: boolean;
  stretchOnHover?: boolean;
  fillWidth?: boolean;
};

export function SegmentedControl<
  const Options extends readonly KeyLabelPair[],
>({
  onChange,
  options,
  value,
  isDark,
  stretchOnHover = false,
  fillWidth = false,
}: SegmentedControlProps<Options>) {
  const {
    overlayPillStyle,
    optionRefs,
    containerRef,
    onOptionMouseLeave,
    onOptionMouseOver,
    overlayPillRef,
  } = useSegmentedControl({ value, stretchOnHover });

  return (
    <div
      ref={containerRef}
      className={clsx(
        "rounded-full",
        isDark ? "bg-font-secondary" : "bg-canvas-secondary",
        fillWidth ? "w-full" : "w-fit",
        "flex",
        "flex-row",
        "items-center",
        "relative",
      )}
    >
      <div
        className="absolute bg-primary top-0 bottom-0 duration-250 ease-in-out rounded-full"
        ref={overlayPillRef}
        style={{
          left: overlayPillStyle.left,
          width: overlayPillStyle.width,
        }}
      />
      {options.map((option) => (
        <button
          key={`segmented-control-option-${option.key}`}
          onMouseOver={() => onOptionMouseOver(option.key)}
          onMouseLeave={onOptionMouseLeave}
          ref={(element) => {
            if (element === null) optionRefs.current.delete(option.key);
            else optionRefs.current.set(option.key, element);
          }}
          className={clsx(
            "rounded-full",
            fillWidth && "flex-1",
            "z-10",
            "cursor-pointer",
            "py-1",
            "px-6",
            "font-medium",
            "duration-200",
            "ease-in-out",
            "text-lg",
            isDark ? "text-font-white" : "text-font-secondary",
            value === option.key && "text-font-white",
          )}
          onClick={() => onChange(option.key)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
